import { useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import api from "@/services/api";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Upload, CheckCircle, X, Eye } from "lucide-react";
import {
    getQuestionnaire,
    saveQuestionnaireAnswers,
    uploadQuestionnaireFile,
    deleteQuestionnaireFile,
    syncQuestionnaireFromSalesforce,
    type CvQuestion,
} from "@/services/universityService";
import FilePreviewModal from "@/pages/portal/components/FilePreviewModal";

const DEFAULT_ACCEPTED_TYPES = ".pdf,.jpg,.jpeg,.png";
const QUESTIONNAIRE_DATE_RANGE_YEARS = 75;

function formatDateForInput(inputDate: Date): string {
    const year = inputDate.getFullYear();
    const month = String(inputDate.getMonth() + 1).padStart(2, "0");
    const day = String(inputDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function getQuestionnaireMinDate(): string {
    const currentDate = new Date();
    const minDate = new Date(
        currentDate.getFullYear() - QUESTIONNAIRE_DATE_RANGE_YEARS,
        currentDate.getMonth(),
        currentDate.getDate(),
    );
    return formatDateForInput(minDate);
}

function getQuestionnaireMaxDate(): string {
    const currentDate = new Date();
    const maxDate = new Date(
        currentDate.getFullYear() + QUESTIONNAIRE_DATE_RANGE_YEARS,
        currentDate.getMonth(),
        currentDate.getDate(),
    );
    return formatDateForInput(maxDate);
}

function resolveAcceptTypes(value: string | null | undefined): string {
    if (!value || !value.trim()) return DEFAULT_ACCEPTED_TYPES;
    const normalized = value.split(";").map((t) => t.trim()).filter(Boolean).join(",");
    return normalized || DEFAULT_ACCEPTED_TYPES;
}

function formatLabel(value: string | null | undefined): string {
    if (!value || !value.trim()) return "PDF, JPG, PNG";
    return value.split(";").map((t) => t.trim().replace(/^\./, "").toUpperCase()).filter(Boolean).join(", ");
}

export interface QuestionnaireHandle {
    save: () => Promise<void>;
}

interface QuestionnaireProps {
    appNumber: string;
    onRequirementChange?: (met: boolean) => void;
}

export default forwardRef<QuestionnaireHandle, QuestionnaireProps>(function Questionnaire(
    { appNumber, onRequirementChange },
    ref
) {
    const [questions, setQuestions] = useState<CvQuestion[]>([]);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const checkRequirements = (
        currentAnswers: Record<string, string>,
        currentFiles: Record<string, File>,
        currentQuestions: CvQuestion[],
    ) => {
        const met = currentQuestions
            .filter((q) => q.required)
            .every((q) =>
                q.question_data_type === "File Upload"
                    ? q.id in currentFiles || !!q.answer
                    : (currentAnswers[q.id] ?? "").trim() !== "",
            );
        onRequirementChange?.(met);
    };

    useEffect(() => {
        if (!appNumber) return;

        let cancelled = false;
        setLoading(true);
        setError(null);

        const loadFromDb = () => {
            getQuestionnaire(appNumber)
                .then((res) => {
                    if (cancelled) return;
                    setQuestions(res.questions);
                    const initial: Record<string, string> = {};
                    res.questions.forEach((q) => {
                        if (q.answer != null) initial[q.id] = q.answer;
                    });
                    setAnswers(initial);
                    checkRequirements(initial, {}, res.questions);
                })
                .catch(() => {
                    if (!cancelled) setError("Could not load questionnaire. Please try again.");
                })
                .finally(() => {
                    if (!cancelled) setLoading(false);
                });
        };

        syncQuestionnaireFromSalesforce(appNumber)
            .catch(() => {})
            .finally(() => {
                if (!cancelled) loadFromDb();
            });

        return () => { cancelled = true; };
    }, [appNumber]);

    useImperativeHandle(ref, () => ({
        save: async () => {
            if (!appNumber || questions.length === 0) return;

            const textQuestions = questions.filter((q) => q.question_data_type !== "File Upload");
            const payload = textQuestions
                .map((q) => ({ salesforce_id: q.id, answer: answers[q.id] ?? "" }))
                .filter(({ answer }) => answer !== "");
            if (payload.length > 0) {
                try {
                    await saveQuestionnaireAnswers(appNumber, payload);
                } catch (e) {
                    console.error("Failed to save questionnaire answers:", e);
                }
            }

            const fileEntries = Object.entries(pendingFiles);
            await Promise.all(
                fileEntries.map(async ([questionId, file]) => {
                    try {
                        await uploadQuestionnaireFile(appNumber, questionId, file);
                        setPendingFiles((prev) => {
                            const next = { ...prev };
                            delete next[questionId];
                            return next;
                        });
                    } catch (e) {
                        console.error(`Failed to upload file for question ${questionId}:`, e);
                    }
                })
            );
        },
    }));

    const setAnswer = (id: string, value: string) => {
        setAnswers((prev: Record<string, string>) => {
            const next = { ...prev, [id]: value };
            checkRequirements(next, pendingFiles, questions);
            return next;
        });
    };

    const setPendingFile = (id: string, file: File | null) => {
        setPendingFiles((prev) => {
            const next = { ...prev };
            if (file) next[id] = file;
            else delete next[id];
            checkRequirements(answers, next, questions);
            return next;
        });
    };

    const sections = questions.reduce<Record<string, CvQuestion[]>>((acc, q) => {
        const key = q.section_name || "General";
        (acc[key] ??= []).push(q);
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="space-y-8 md:space-y-12">
                <div>
                    <h2 className="text-2xl md:text-4xl font-serif font-black text-[#001B44] leading-tight text-left">
                        Questionnaire
                    </h2>
                </div>
                <div className="flex flex-col gap-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-12 rounded-xl bg-slate-100 animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 md:space-y-12">
            <div>
                <h2 className="text-2xl md:text-4xl font-serif font-black text-[#001B44] leading-tight text-left">
                    Questionnaire
                </h2>
                <p className="text-slate-900 mt-2 md:mt-4 text-base md:text-lg font-medium max-w-2xl text-left">
                    Please answer the following questions about your application.
                </p>
            </div>

            {error && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-800">
                    {error}
                </div>
            )}

            {!error && questions.length === 0 && !loading && (
                <div className="text-center py-12 border-2 border-dashed border-slate-100 rounded-3xl bg-slate-50/30">
                    <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                        No questionnaire required for this application
                    </p>
                </div>
            )}

            {Object.entries(sections).map(([sectionName, sectionQuestions]) => {
                const requiredQuestions = sectionQuestions.filter(question => question.required);
                const optionalQuestions = sectionQuestions.filter(question => !question.required);
                
                return (
                <section key={sectionName} className="space-y-6">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#001B44]" />
                        {sectionName}
                    </h4>

                    {requiredQuestions.length > 0 && (
                        <div className="rounded-2xl border border-red-100 bg-red-50/30 p-4 space-y-4">
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] font-black uppercase tracking-widest text-red-600 bg-red-50 px-2 py-1 rounded-md border border-red-100">
                                    Required
                                </span>
                                <span className="text-[9px] text-slate-400">
                                    {requiredQuestions.length} {requiredQuestions.length === 1 ? 'item' : 'items'}
                                </span>
                            </div>
                            {requiredQuestions.map((question) => (
                                <div key={question.id} className="space-y-2 w-full max-w-md pl-3 border-l-2 border-red-200">
                                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1 flex items-center gap-2">
                                        {question.question}
                                        <span className="text-[8px] font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded">REQUIRED</span>
                                    </label>
                                    {question.description && (
                                        <p className="text-xs text-slate-500 ml-1">{question.description}</p>
                                    )}
                                    {question.question_data_type === "File Upload" ? (
                                        <FileUploadField
                                            question={question}
                                            pendingFile={pendingFiles[question.id] ?? null}
                                            onFileChange={(file) => setPendingFile(question.id, file)}
                                            appNumber={appNumber}
                                            onRemoved={() => {
                                                setQuestions((prev) => {
                                                    const next = prev.map((item: CvQuestion) =>
                                                        item.id === question.id
                                                            ? { ...item, answer: null, public_url: null, document_id: null }
                                                            : item
                                                    );
                                                    checkRequirements(answers, pendingFiles, next);
                                                    return next;
                                                });
                                            }}
                                        />
                                    ) : (
                                        <QuestionField
                                            question={question}
                                            value={answers[question.id] ?? ""}
                                            onChange={(v) => setAnswer(question.id, v)}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {optionalQuestions.length > 0 && (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4 space-y-4">
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-1 rounded-md border border-slate-200">
                                    Optional
                                </span>
                                <span className="text-[9px] text-slate-400">
                                    {optionalQuestions.length} {optionalQuestions.length === 1 ? 'item' : 'items'}
                                </span>
                            </div>
                            {optionalQuestions.map((question) => (
                                <div key={question.id} className="space-y-2 w-full max-w-md pl-3 border-l-2 border-slate-200">
                                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1 flex items-center gap-2">
                                        {question.question}
                                        <span className="text-[8px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">OPTIONAL</span>
                                    </label>
                                    {question.description && (
                                        <p className="text-xs text-slate-500 ml-1">{question.description}</p>
                                    )}
                                    {question.question_data_type === "File Upload" ? (
                                        <FileUploadField
                                            question={question}
                                            pendingFile={pendingFiles[question.id] ?? null}
                                            onFileChange={(file) => setPendingFile(question.id, file)}
                                            appNumber={appNumber}
                                            onRemoved={() => {
                                                setQuestions((prev) => {
                                                    const next = prev.map((item: CvQuestion) =>
                                                        item.id === question.id
                                                            ? { ...item, answer: null, public_url: null, document_id: null }
                                                            : item
                                                    );
                                                    checkRequirements(answers, pendingFiles, next);
                                                    return next;
                                                });
                                            }}
                                        />
                                    ) : (
                                        <QuestionField
                                            question={question}
                                            value={answers[question.id] ?? ""}
                                            onChange={(v) => setAnswer(question.id, v)}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </section>
                );
            })}
        </div>
    );
});


function FileUploadField({
    question,
    pendingFile,
    onFileChange,
    appNumber,
    onRemoved,
}: {
    question: CvQuestion;
    pendingFile: File | null;
    onFileChange: (file: File | null) => void;
    appNumber: string;
    onRemoved: () => void;
}) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
    const [localMimeType, setLocalMimeType] = useState<string | null>(null);
    const [removeUploading, setRemoveUploading] = useState(false);
    const [authenticatedBlobUrl, setAuthenticatedBlobUrl] = useState<string | null>(null);
    const [authenticatedMimeType, setAuthenticatedMimeType] = useState<string | null>(null);

    const alreadyUploaded = !!question.answer;
    const hasFile = pendingFile !== null;

    const fetchBlobUrl = useCallback(async (url: string) => {
        try {
            const response = await api.get(url.replace(/^\/api/, ""), { responseType: "blob" });
            const blob = response.data as Blob;
            const mime = (response.headers["content-type"] as string | undefined)?.split(";")[0].trim() || blob.type || null;
            setAuthenticatedMimeType(mime);
            setAuthenticatedBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
        } catch {
            setAuthenticatedBlobUrl(null);
            setAuthenticatedMimeType(null);
        }
    }, []);

    useEffect(() => {
        if (alreadyUploaded && question.public_url && !hasFile) {
            fetchBlobUrl(question.public_url);
        }
        return () => {
            setAuthenticatedBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
        };
    }, [alreadyUploaded, question.public_url, hasFile, fetchBlobUrl]);

    useEffect(() => {
        return () => {
            setLocalPreviewUrl((prev: string | null) => { if (prev) URL.revokeObjectURL(prev); return null; });
        };
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        if (file) {
            setLocalPreviewUrl((prev: string | null) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(file); });
            setLocalMimeType(file.type || null);
        } else {
            setLocalPreviewUrl((prev: string | null) => { if (prev) URL.revokeObjectURL(prev); return null; });
            setLocalMimeType(null);
        }
        onFileChange(file);
    };

    const handleRemove = () => {
        onFileChange(null);
        setLocalPreviewUrl((prev: string | null) => { if (prev) URL.revokeObjectURL(prev); return null; });
        setLocalMimeType(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const activePreviewUrl = localPreviewUrl ?? (alreadyUploaded ? authenticatedBlobUrl : null);

    if (hasFile) {
        return (
            <>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span className="text-xs font-bold text-slate-800 truncate">{pendingFile.name}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {localPreviewUrl && (
                            <button
                                type="button"
                                onClick={() => setPreviewOpen(true)}
                                className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-500 hover:text-[#001B44] bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-lg transition-all"
                            >
                                <Eye className="w-3 h-3" /> View
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleRemove}
                            className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 px-2 py-0.5 rounded-lg transition-all"
                        >
                            <X className="w-3 h-3" /> Remove
                        </button>
                    </div>
                </div>
                {previewOpen && activePreviewUrl && (
                    <FilePreviewModal
                        url={activePreviewUrl}
                        fileName={pendingFile.name}
                        mimeType={localMimeType ?? undefined}
                        onClose={() => setPreviewOpen(false)}
                    />
                )}
            </>
        );
    }

    if (alreadyUploaded) {
        const handleRemoveUploaded = async () => {
            setRemoveUploading(true);
            try {
                await deleteQuestionnaireFile(appNumber, question.id);
                onRemoved();
            } catch {
                // silently ignore — user can retry by replacing the file
            } finally {
                setRemoveUploading(false);
            }
        };

        return (
            <>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span className="text-xs font-bold text-slate-800 truncate">
                            {question.answer}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {authenticatedBlobUrl && (
                            <button
                                type="button"
                                onClick={() => setPreviewOpen(true)}
                                className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-500 hover:text-[#001B44] bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-lg transition-all"
                            >
                                <Eye className="w-3 h-3" /> View
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleRemoveUploaded}
                            disabled={removeUploading}
                            className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 px-2 py-0.5 rounded-lg transition-all disabled:opacity-50"
                        >
                            <X className="w-3 h-3" /> {removeUploading ? "…" : "Remove"}
                        </button>
                        <label className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 px-2 py-0.5 rounded-lg transition-all cursor-pointer">
                            Replace
                            <input ref={inputRef} type="file" accept={resolveAcceptTypes(question.picklist_values)} onChange={handleChange} className="sr-only" />
                        </label>
                    </div>
                </div>
                {previewOpen && authenticatedBlobUrl && (
                    <FilePreviewModal
                        url={authenticatedBlobUrl}
                        fileName={question.answer ?? undefined}
                        mimeType={authenticatedMimeType ?? undefined}
                        onClose={() => setPreviewOpen(false)}
                    />
                )}
            </>
        );
    }

    return (
        <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl p-5 cursor-pointer hover:border-[#001B44]/40 hover:bg-slate-50 transition-all">
            <Upload className="w-5 h-5 text-slate-400" />
            <span className="text-xs font-bold text-slate-500">Click to upload · {formatLabel(question.picklist_values)} · max 10 MB</span>
            <input ref={inputRef} type="file" accept={resolveAcceptTypes(question.picklist_values)} onChange={handleChange} className="sr-only" />
        </label>
    );
}


function QuestionField({
    question,
    value,
    onChange,
}: {
    question: CvQuestion;
    value: string;
    onChange: (v: string) => void;
}) {
    const type = question.question_data_type ?? "Text";

    if (type === "Picklist" && question.picklist_values) {
        const options = question.picklist_values.split(";").map((o) => o.trim()).filter(Boolean);
        return (
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger className="h-12 rounded-xl border-slate-200 bg-white font-bold">
                    <SelectValue placeholder="Select an answer" />
                </SelectTrigger>
                <SelectContent>
                    {options.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                            {opt}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }

    if (type === "Boolean") {
        return (
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger className="h-12 rounded-xl border-slate-200 bg-white font-bold">
                    <SelectValue placeholder="Select an answer" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                </SelectContent>
            </Select>
        );
    }

    if (type === "Date") {
        return (
            <Input
                type="date"
                value={value}
                min={getQuestionnaireMinDate()}
                max={getQuestionnaireMaxDate()}
                onChange={(e) => onChange(e.target.value)}
                className="h-12 rounded-xl border-slate-200 font-bold"
            />
        );
    }

    if (type === "Number") {
        return (
            <Input
                type="number"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-12 rounded-xl border-slate-200 font-bold"
            />
        );
    }

    if (type === "Multiselect" && question.picklist_values) {
        const options = question.picklist_values.split(";").map((o) => o.trim()).filter(Boolean);
        const selected = value ? value.split(";").filter(Boolean) : [];
        const toggle = (opt: string) => {
            const next = selected.includes(opt)
                ? selected.filter((s) => s !== opt)
                : [...selected, opt];
            onChange(next.join(";"));
        };
        return (
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                {options.map((opt) => (
                    <label
                        key={opt}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition text-sm ${
                            selected.includes(opt)
                                ? "bg-primary/5 text-primary font-bold"
                                : "text-slate-700 hover:bg-slate-50 font-medium"
                        }`}
                    >
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition ${
                            selected.includes(opt) ? "bg-primary border-primary" : "border-slate-300"
                        }`}>
                            {selected.includes(opt) && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                        </div>
                        <input type="checkbox" className="hidden" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                        {opt}
                    </label>
                ))}
            </div>
        );
    }

    if (type === "Long Text") {
        return (
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={4}
                placeholder="Your answer…"
                className="w-full h-auto rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-bold resize-none outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
        );
    }

    // Default: Text
    return (
        <Input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="h-12 rounded-xl border-slate-200 font-bold"
        />
    );
}
